import qs from 'qs';
import { defaultGateway } from 'store/ticketingPublic';
import { joinPaths, accessNested } from 'utils/utils';
import { handleApiResponse } from 'utils/handleApi';
import deserialise from 'utils/deserialise';


const IS_DEMO = typeof window !== 'undefined' && window.__INITIAL_STATE__ &&
	window.__INITIAL_STATE__.appData.options.isDemoTicketing;

// const API_BASE = 'http://ticketing.docker.labs:60080/mxt';
// const API_BASE_DEV = 'https://labs.powster.com/mxt';
// const API_BASE = 'http://ticketing.192.168.1.11.xip.io/mxt';
// const API_BASE_DEV = 'https://live-ticketing.labs.powster.io/mxt';
const API_BASE_DEV = 'https://ticketing.staging.ticketing.powster.io/mxt'; // since 19-03-2018
const API_BASE_PROD = 'https://ticketing.powster.com/mxt';
const API_BASE = IS_DEMO ? API_BASE_DEV : API_BASE_PROD;
// const API_BASE = 'http://ticketing.docker.localhost:60080/mxt'; // When working locally
// const API_BASE = 'http://192.168.1.36:5000/mxt'; // When working locally

const API_HEADERS = {
	'Accept':       'application/vnd.api+json',
	'Content-Type': 'application/vnd.api+json'
};


let dummyExhibitor = '00000000-0000-0000-0000-000000000000';
let dummyTheater   = '00000000-0000-0000-0000-000000000000';
let dummyScreening = '00000000-0000-0000-0000-000000000000';

// Load in test data
if (IS_DEMO) {
	const params = qs.parse(window.location.search.substr(1));
	const gateway = params.gateway !== undefined ? params.gateway : defaultGateway;
	const seating = params.seating === 'false' ? false : true;
	getTestData(seating, gateway);
}

/**
 * @typedef {Object} App
 * @property {!string} studio - the studio slug
 * @property {!string} movie - the movie slug
 * @property {!string} region - the region slug
 */

/**
 * Return an objet representing the application.
 *
 * @return {App} the app object
 */
const getApp = () => {
	const meta = accessNested(__INITIAL_STATE__, 'appData.meta');
	if (!meta) return;

	const studio = accessNested(meta, 'studio.slug');
	const movie = accessNested(meta, 'title.slug');
	const region = accessNested(meta, 'region.slug');
	if (!studio || !movie || !region) return;

	return { studio, movie, region };
};

const getPiwikId = () => {
	if (!document || !document.cookie) return;
	const cookies = document.cookie.split('; ');
	for (const cookie of cookies) {
		const [key, value] = cookie.split('=');
		if (key.startsWith('_pk_id')) {
			return value.substr(0, 16);
		}
	}
};

/**
 * @typedef {Object} TicketingAnalytics
 * @property {!string} piwikId - the Piwik id, taken from the cookies
 * @property {!string} referrer - the page referrer (unchanged)
 * @property {!string} device - the type of device of the user
 * @property {string} campaign - the campaign tag, taken from the query string
**/

/**
 * Return an object with stats for the ticketing database.
 *
 * @return {TicketingAnalytics} an object containing analytics information
 */
const getAnalytics = () => {
	const piwikId = getPiwikId();
	const referrer = document.referrer;

	const deviceWidth = window.innerWidth;
	const device = deviceWidth > 1024 ? 'desktop' : deviceWidth > 768 ? 'tablet' : 'phone';

	const params = qs.parse(window.location.search.substr(1));
	const campaign = params.campaign;

	const analytics = { piwikId, referrer, device };
	if (typeof campaign === 'string') analytics.campaign = campaign;

	return analytics;
};


/* Api interaction
---------------------------*/

// TODO: We need some catch() overhere!

const apiGet = (apiPath) => {
	return fetch(joinPaths(API_BASE, apiPath), {
		method:  'GET',
		headers: API_HEADERS
	})
		.then(handleApiResponse)
		.then(deserialise);
};

const apiPost = (apiPath, body) => {
	return fetch(joinPaths(API_BASE, apiPath), {
		method:  'POST',
		headers: API_HEADERS,
		body:    JSON.stringify(body)
	})
		.then(handleApiResponse)
		.then(deserialise);
};

const apiDelete = (apiPath, body) => {
	return fetch(joinPaths(API_BASE, apiPath), {
		method:  'DELETE',
		headers: API_HEADERS,
		body:    JSON.stringify(body)
	})
		.then(handleApiResponse)
		.then(deserialise);
};

const apiPut = (apiPath, body) => {
	return fetch(joinPaths(API_BASE, apiPath), {
		method:  'PUT',
		headers: API_HEADERS,
		body:    JSON.stringify(body)
	})
		.then(handleApiResponse)
		.then(deserialise);
};

const apiPatch = (apiPath, body) => {
	return fetch(joinPaths(API_BASE, apiPath), {
		method:  'PATCH',
		headers: API_HEADERS,
		body:    JSON.stringify(body)
	})
		.then(handleApiResponse)
		.then(deserialise);
};

function getTestData(seating, gateway) {
	const query = { seating, gateway };

	fetch(joinPaths(API_BASE, '/devget?' + qs.stringify(query)), {
		method: 'GET',
		headers: API_HEADERS
	})
		.then(handleApiResponse)
		.then(response => {
			const dummy = response.data[0];
			if (dummy) {
				dummyExhibitor = dummy.provider_exhibitor_id;
				dummyTheater   = dummy.provider_theater_id;
				dummyScreening = dummy.provider_screening_id;
			}
		});
}


/* Ticket Types
---------------------------*/

export const getTicketTypes = (exhibitorId, screeningId, authToken) => {
	const query = {
		exhibitor: IS_DEMO ? dummyExhibitor : exhibitorId,
		screening: IS_DEMO ? dummyScreening : screeningId
	};

	if (authToken) query.token = authToken;

	return apiGet(`/ticket_types?${qs.stringify(query)}`);
};


/* Orders
---------------------------*/

export const createOrder = (exhibitorId, theaterId, screeningId, seatingSupported, tickets,
	authToken) => {

	if (!Array.isArray(tickets)) {
		// make it an array if it's an object
		tickets = Object.keys(tickets).map(key => tickets[key]);
	}

	// Let's get what we need for the push
	const orderTickets = tickets.map(ticket => {
		return {
			type:     ticket.type,
			quantity: ticket.quantity
		};
	});

	const body = {
		exhibitor: IS_DEMO ? dummyExhibitor : exhibitorId,
		theater:   IS_DEMO ? dummyTheater : theaterId,
		screening: IS_DEMO ? dummyScreening : screeningId,
		app:       getApp(),
		seating:   seatingSupported,
		tickets:   orderTickets,
		analytics: getAnalytics()
	};

	if (authToken) body.token = authToken;

	return apiPost('/order?nocache=' + Math.random(), body);
};

export const updateTickets = (exhibitorId, screeningId, orderId, seatingSupported,
	ticketsToAdd, ticketIdsToRemove, authToken) => {

	const body = {
		exhibitor: IS_DEMO ? dummyExhibitor : exhibitorId,
		screening: IS_DEMO ? dummyScreening : screeningId,
		order:     orderId,
		seating:   seatingSupported
	};

	if (Array.isArray(ticketsToAdd) && ticketsToAdd.length > 0) {
		body.ticketsToAdd = ticketsToAdd;
	}
	if (Array.isArray(ticketIdsToRemove) && ticketIdsToRemove.length > 0) {
		body.ticketIdsToRemove = ticketIdsToRemove;
	}
	if (authToken) body.token = authToken;

	return apiPatch('/tickets?nocache=' + Math.random(), body);
};

export const cancelOrder = (exhibitorId, orderId, authToken, async = false) => {
	const body = {
		exhibitor: IS_DEMO ? dummyExhibitor : exhibitorId,
		order:     orderId
	};

	if (authToken) body.token = authToken;

	// We need to send it as a synchronous call because this function gets called on unload
	// it won't finish the call if the call is async...
	// TODO: make this async if needed (for the close button)
	if (async) {
		return apiDelete('/order', body);
	} else {
		const xhr = new XMLHttpRequest();
		xhr.open('DELETE', joinPaths(API_BASE, '/order'), false);
		xhr.setRequestHeader('Accept', 'application/vnd.api+json');
		xhr.setRequestHeader('Content-Type', 'application/vnd.api+json');
		xhr.send(JSON.stringify(body));
	}
};

export const addTickets = (exhibitorId, screeningId, orderId, seatingSupported, ticketsToAdd) => {
	const body = {
		exhibitor: IS_DEMO ? dummyExhibitor : exhibitorId,
		screening: IS_DEMO ? dummyScreening : screeningId,
		order:     orderId,
		seating:   seatingSupported,
		tickets:   ticketsToAdd
	};

	return apiPost('/tickets?nocache=' + Math.random(), body);
};

export const removeTickets = (exhibitorId, screeningId, orderId, seatingSupported, ticketIdsToRemove) => {
	const body = {
		exhibitor: IS_DEMO ? dummyExhibitor : exhibitorId,
		screening: IS_DEMO ? dummyScreening : screeningId,
		order:     orderId,
		seating:   seatingSupported,
		ids:       ticketIdsToRemove
	};

	return apiDelete('/tickets?nocache=' + Math.random(), body);
};


/* Seats
---------------------------*/

export const updateSeats = (exhibitorId, screeningId, orderId, seats, authToken) => {
	const body = {
		exhibitor: IS_DEMO ? dummyExhibitor : exhibitorId,
		screening: IS_DEMO ? dummyScreening : screeningId,
		order:     orderId,
		seats:     seats
	};

	if (authToken) body.token = authToken;

	return apiPut('/seats', body);
};

export const getSeatingMap = (exhibitorId, screeningId, orderId, authToken) => {
	const body = {
		exhibitor: IS_DEMO ? dummyExhibitor : exhibitorId,
		screening: IS_DEMO ? dummyScreening : screeningId,
		order:     orderId
	};

	if (authToken) body.token = authToken;

	return apiGet(`/seat_map?${qs.stringify(body)}`);
};


/* Transaction
---------------------------*/

export const startTransaction = (exhibitorId, theaterId, orderId, paymentArgs,
	name = '', email = '', phone = '', /*address = '', zip = '', city = '', loyaltyNumber = '',*/
	authToken) => {

	const body = {
		exhibitor:   IS_DEMO ? dummyExhibitor : exhibitorId,
		theater:     IS_DEMO ? dummyTheater : theaterId,
		order:       orderId,
		paymentArgs: paymentArgs
	};

	if (typeof name === 'string' && name.length > 0) {
		body.name = name;
	}
	if (typeof email === 'string' && email.length > 0) {
		body.email = email;
	}
	if (typeof phone === 'string' && phone.length > 0) {
		body.phone = phone;
	}
	// if (typeof address === 'string' && address.length > 0) {
	// 	if (address.length > 36) {
	// 		body.address1 = address.substring(0, 36);
	// 		body.address2 = address.substring(36, 72);
	// 	} else {
	// 		body.address1 = address;
	// 	}
	// }
	// if (typeof zip === 'string' && zip.length > 0) {
	// 	body.zip = zip;
	// }
	// if (typeof city === 'string' && city.length > 0) {
	// 	body.city = city;
	// }
	// if (typeof loyaltyNumber === 'string' && loyaltyNumber.length > 0) {
	// 	body.loyalty = loyaltyNumber;
	// }
	if (authToken) body.token = authToken;

	return apiPost('/start_transaction?nocache=' + Math.random(), body);
};

export const completeOrder = (exhibitorId, theaterId, orderId, paymentResponse, authToken) => {
	const body = {
		exhibitor:       IS_DEMO ? dummyExhibitor : exhibitorId,
		theater:         IS_DEMO ? dummyTheater : theaterId,
		order:           orderId,
		paymentResponse: paymentResponse
	};

	if (authToken) body.token = authToken;

	return apiPost('/complete_order?nocache=' + Math.random(), body);
};
